import React, { useEffect, useState } from 'react';
import Navbar from '../components/Navbar';
import BungalowCard from '../components/BungalowCard';
import ProgressBar from '../components/ProgressBar';
import API from '../services/api';
import { IconBarChart, IconCheckCircle, IconClock, IconTrendingUp, IconCalendar } from '../components/Icons';
import { formatDate, inputDateOrDefault, isDateOverdue } from '../utils/dateUtils';

function ProjectPage({ projectCode }) {
  const projectName = projectCode === 'SKY' ? 'Skyora Karjat' : 'Sahyadri Sylvan Villas';
  const [project, setProject] = useState(null);
  const [plots, setPlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [editingTarget, setEditingTarget] = useState(false);
  const [targetDate, setTargetDate] = useState('');
  const [savingTarget, setSavingTarget] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await API.get(`/projects/${projectCode}`);
        const data = res.data.project;
        setProject(data);
        setPlots(data.plots || []);
        setTargetDate(inputDateOrDefault(data.target_completion_date));
      } catch (err) {
        setError('Failed to load project data.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [projectCode]);

  const handleSaveTargetDate = async () => {
    setSavingTarget(true);
    try {
      await API.put(`/projects/${projectCode}/target-date`, { target_date: targetDate || null });
      setProject(prev => ({ ...prev, target_completion_date: targetDate || null }));
      setEditingTarget(false);
    } catch (err) {
      alert('Failed to update target date.');
    } finally {
      setSavingTarget(false);
    }
  };

  const handleClearTargetDate = () => {
    setTargetDate('');
  };


  const isOverdue = (plot) =>
    plot.target_completion_date
    && plot.status !== 'completed'
    && isDateOverdue(plot.target_completion_date);

  const filteredPlots = plots.filter((plot) => {
    const matchesFilter =
      filter === 'all' ||
      (filter === 'delayed' ? isOverdue(plot) : plot.status === filter);
    const matchesSearch = plot.bungalow_number?.toLowerCase().includes(search.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const completedCount = plots.filter(p => p.status === 'completed').length;
  const activeCount = plots.filter(p => p.status === 'active').length;
  const notStartedCount = plots.filter(p => p.status === 'not_started').length;
  const delayedCount = plots.filter(p => isOverdue(p)).length;
  const avgCompletion = plots.length > 0
    ? Math.round(plots.reduce((sum, p) => sum + (p.overall_completion || 0), 0) / plots.length)
    : 0;

  const isSkyora = projectCode === 'SKY';

  const filterLabels = {
    all: isSkyora ? 'All Units' : 'All Bungalows',
    completed: `Completed (${completedCount})`,
    active: `Active (${activeCount})`,
    not_started: `Not Started (${notStartedCount})`,
    delayed: `Delayed (${delayedCount})`
  };

  return (
    <div className="page">
      <Navbar />
      <main className="main-content">
        <div className="project-page-header">
          <div className="project-page-breadcrumb">PROJECT BOARD</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <h1 className="page-title">{projectName}</h1>
            {project?.target_completion_date
              && isDateOverdue(project.target_completion_date)
              && plots.some(p => p.status !== 'completed') && (
              <span className="project-delayed-badge">⚠ Delayed</span>
            )}
          </div>
          <div className="project-target-row">
            <IconCalendar size={14} />
            <span className="project-target-label">Project Target Completion:</span>
            {!editingTarget ? (
              <>
                {project?.target_completion_date
                  ? <span className="project-target-date">{formatDate(project.target_completion_date)}</span>
                  : <span className="project-target-empty">Not set</span>
                }
                <button className="project-target-btn" onClick={() => { setEditingTarget(true); setTargetDate(inputDateOrDefault(project?.target_completion_date)); }}>
                  {project?.target_completion_date ? 'CHANGE PROJECT TARGET' : 'SET PROJECT TARGET'}
                </button>
              </>
            ) : (
              <>
                <input
                  type="date"
                  className="project-target-input"
                  value={targetDate}
                  onChange={e => setTargetDate(e.target.value)}
                />
                <button className="project-target-save-btn" onClick={handleSaveTargetDate} disabled={savingTarget}>
                  {savingTarget ? 'Saving...' : 'Save'}
                </button>
                {project?.target_completion_date && (
                  <button className="project-target-cancel-btn" onClick={handleClearTargetDate} disabled={savingTarget}>
                    Clear
                  </button>
                )}
                <button className="project-target-cancel-btn" onClick={() => setEditingTarget(false)}>
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>

        {loading && <div className="loading-spinner">Loading project...</div>}
        {error && <div className="error-message">{error}</div>}

        {!loading && !error && (
          <>
            <div className="project-overview-bar">
              <div className="project-overview-item">
                <div className="project-overview-label">
                  <span className="overview-icon overview-icon--green"><IconBarChart size={14} /></span>
                  CONSTRUCTION PROGRESS
                </div>
                <div className="project-overview-value large">{avgCompletion}%</div>
                <ProgressBar percentage={avgCompletion} color="#009966" height="6px" />
              </div>
              <div className="project-overview-divider" />
              <div className="project-overview-item">
                <div className="project-overview-label">
                  <span className="overview-icon overview-icon--green"><IconCheckCircle size={14} /></span>
                  HANDOVER COMPLETED
                </div>
                <div className="project-overview-value green">{completedCount} <span className="overview-total">/ {plots.length}</span></div>
              </div>
              <div className="project-overview-divider" />
              <div className="project-overview-item">
                <div className="project-overview-label">
                  <span className="overview-icon overview-icon--amber"><IconTrendingUp size={14} /></span>
                  {isSkyora ? 'ACTIVE UNITS' : 'ACTIVE BUNGALOWS'}
                </div>
                <div className="project-overview-value amber">{activeCount} <span className="overview-total">/ {plots.length}</span></div>
              </div>
              <div className="project-overview-divider" />
              <div className="project-overview-item">
                <div className="project-overview-label">
                  <span className="overview-icon overview-icon--gray"><IconClock size={14} /></span>
                  NOT STARTED
                </div>
                <div className="project-overview-value gray">{notStartedCount} <span className="overview-total">/ {plots.length}</span></div>
              </div>
            </div>

            <div className="plot-controls">
              <div className="plot-filter-row">
                <span className="filter-label">FILTER:</span>
                <div className="plot-filter-tabs">
                  {Object.entries(filterLabels).map(([key, label]) => (
                    <button
                      key={key}
                      className={`filter-tab ${filter === key ? 'active' : ''}`}
                      onClick={() => setFilter(key)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <input
                className="plot-search"
                type="text"
                placeholder={isSkyora ? 'Search Unit No.' : 'Search Bungalow No.'}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className="plots-grid-section">
              <h2 className="grid-section-title">{isSkyora ? 'Unit Grid Layout' : 'Bungalow Grid Layout'}</h2>
              <p className="grid-section-subtitle">Green is Completed, Yellow is Active, and Grey is Not Started.</p>

              <div className="plots-grid">
                {filteredPlots.length === 0 ? (
                  <div className="no-plots">{isSkyora ? 'No units found matching your criteria.' : 'No bungalows found matching your criteria.'}</div>
                ) : (
                  filteredPlots.map((plot) => <BungalowCard key={plot.id} plot={plot} projectCode={projectCode} />)
                )}
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

export default ProjectPage;
